import React from 'react';
import { Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import { Container, Tabs, Tab, Box, Typography, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Profile from './Profile';
import BookingsHistory from './History';
import Subscription from './Subscription';
import Checkout from './Checkout';
import {useTranslation} from "react-i18next";
import { currentAccount } from '../../helpers/accounts';

const Account = () => {
    const location = useLocation();
    const { t } = useTranslation();

    // Programme membership is free, so there is no subscription to manage.
    const account = currentAccount();
    const inProgramme = Boolean(account && account.programName);

    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('sm'));
    const tabsVariant = isXs ? 'scrollable' : 'fullWidth';
    const scrollButtons = isXs ? 'auto' : false;

    // Match the current route to highlight the active tab
    const activeTab = location.pathname.split('/').pop() || 'checkout';

    return (
        <Box
            sx={{
                background: 'linear-gradient(to bottom right, #f0f4f8, #e9eff5)',
                minHeight: '100vh',
                py: { xs: 4, md: 8 },
                px: { xs: 2, sm: 4 },
            }}
        >
            <Container maxWidth="md">
                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 2, md: 4 },
                        borderRadius: 3,
                        backgroundColor: 'white',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                    }}
                >
                    <Typography
                        variant="h4"
                        mb={4}
                        sx={{
                            textAlign: 'center',
                            fontWeight: 600,
                            color: '#222',
                            letterSpacing: 0.5,
                        }}
                    >
                        {t('profile.account.title')}
                    </Typography>
                    {inProgramme && (
                        <Typography
                            variant="body2"
                            sx={{ textAlign: 'center', color: 'primary.main', fontWeight: 600, mt: -3, mb: 3 }}
                        >
                            {account.programName}
                            {account.bookAllowance
                                ? ` · ${t('program.allowance', { count: account.bookAllowance })}`
                                : ''}
                        </Typography>
                    )}
                    {/* Tabs Navigation */}
                    <Tabs
                        value={activeTab}
                        indicatorColor="primary"
                        textColor="primary"
                        variant={tabsVariant}
                        scrollButtons={scrollButtons}
                        allowScrollButtonsMobile
                        sx={{
                            mb: 3,
                            background: '#f1f3f6',
                            borderRadius: 2,
                            px: 0,
                            boxShadow: '0 1px 8px 0 rgba(0,0,0,0.02)',
                            overflowX: 'auto',
                        }}
                    >
                        <Tab
                            label={t('profile.tabs.checkout.title')}
                            value="checkout"
                            component={NavLink}
                            to="/account/checkout"
                            wrapped={isXs}
                            sx={{
                              mx: 1,
                              fontWeight: 500,
                              borderRadius: 1,
                              color: '#444',
                              minWidth: 'auto',
                              px: { xs: 1, sm: 2 },
                              py: { xs: 1, sm: 1.5 },
                              fontSize: { xs: '0.8rem', sm: '0.9rem' },
                              '&.Mui-selected': {
                                color: '#1565c0',
                                background: '#e3f0fb',
                              },
                            }}
                        />
                        <Tab
                            label={t('profile.tabs.history.title')}
                            value="bookings-history"
                            component={NavLink}
                            to="/account/bookings-history"
                            wrapped={isXs}
                            sx={{
                              mx: 1,
                              fontWeight: 500,
                              borderRadius: 1,
                              color: '#444',
                              minWidth: 'auto',
                              px: { xs: 1, sm: 2 },
                              py: { xs: 1, sm: 1.5 },
                              fontSize: { xs: '0.8rem', sm: '0.9rem' },
                              '&.Mui-selected': {
                                color: '#1565c0',
                                background: '#e3f0fb',
                              },
                            }}
                        />
                        {!inProgramme && (
                          <Tab
                            label={t('profile.tabs.subscription.title')}
                            value="subscription"
                            component={NavLink}
                            to="/account/subscription"
                            wrapped={isXs}
                            sx={{
                              mx: 1,
                              fontWeight: 500,
                              borderRadius: 1,
                              color: '#444',
                              minWidth: 'auto',
                              px: { xs: 1, sm: 2 },
                              py: { xs: 1, sm: 1.5 },
                              fontSize: { xs: '0.8rem', sm: '0.9rem' },
                              '&.Mui-selected': {
                                color: '#1565c0',
                                background: '#e3f0fb',
                              },
                            }}
                          />
                        )}
                        <Tab
                            label={t('profile.tabs.profile.title')}
                            value="profile"
                            component={NavLink}
                            to="/account/profile"
                            wrapped={isXs}
                            sx={{
                              mx: 1,
                              fontWeight: 500,
                              borderRadius: 1,
                              color: '#444',
                              minWidth: 'auto',
                              px: { xs: 1, sm: 2 },
                              py: { xs: 1, sm: 1.5 },
                              fontSize: { xs: '0.8rem', sm: '0.9rem' },
                              '&.Mui-selected': {
                                color: '#1565c0',
                                background: '#e3f0fb',
                              },
                            }}
                        />
                    </Tabs>
                    {/* Tab Content */}
                    <Box mt={3} sx={{ minHeight: 300 }}>
                        <Routes>
                            <Route path="profile" element={<Profile />} />
                            <Route path="checkout" element={<Checkout />} />
                            <Route path="bookings-history" element={<BookingsHistory />} />
                            {!inProgramme && <Route path="subscription" element={<Subscription />} />}
                            {/* An old bookmark or a Stripe return URL would
                                otherwise land a programme family on a blank panel. */}
                            <Route path="*" element={<Navigate to="/account/checkout" replace />} />
                        </Routes>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default Account;
